// src/app/components/antenna/antenna-measured-funnel.tsx
//
// The Brand Monitoring funnel hero. Statistical weight from real measurement
// runs, replacing the self-reported snapshot signal that used to lead the page.
//
// Presentation is Deep Signal's HeroMetricsRow, reused verbatim (UPM-145): one
// funnel presentation across the product, so the same three metrics read the
// same way wherever a client meets them. Do not fork the card design here. The
// stat array shape and order are Deep Signal's contract:
// [0] mention, [1] top5, [2] #1 on list, [3] primary recommendation, with
// primary recommendation as the hero card.
//
// When a brand has no usable measurement run this renders the monitoring
// snapshot notice instead, rather than a hero row full of zeroes.
import { AlertCircle } from "lucide-react";
import {
  type MeasuredFunnel,
} from "@/app/services/brand/measured-funnel-api";
import { getModelLabel } from "@/app/constants/models";
import { HeroMetricsRow, type HeroStat } from "../deep-signal/components/hero-metrics-row";

interface AntennaMeasuredFunnelProps {
  data: MeasuredFunnel | null;
  loading: boolean;
  failed: boolean;
  brandName?: string;
  onAskAI: (text: string) => void;
}

function pct(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

function runDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/**
 * Build Deep Signal's four-stat array from the measured funnel payload.
 *
 * Labels and subs are copied from buildFunnelStatsFromApi so the two surfaces
 * are word for word identical. Index 2 (#1 on list) is a required slot in the
 * contract that HeroMetricsRow does not render: it is in the payload and the
 * card definitions, but it has no surface, and #1 is retired from client
 * facing copy. The measured-funnel endpoint does not carry it, so the slot is
 * filled with zero and stays off screen.
 */
function toHeroStats(funnel: Record<string, number>): HeroStat[] {
  const mention = funnel.mention_rate ?? 0;
  const top5 = funnel.top5_rate ?? 0;
  const primary = funnel.primary_rec_rate ?? 0;
  return [
    { value: pct(mention), label: "Appears in response",   sub: "Mention Rate", raw: mention },
    { value: pct(top5),    label: "In top 5 named",        sub: "Top-5 Rate",   raw: top5 },
    { value: "—",          label: "First brand named",     sub: "#1 on List",   raw: 0 },
    { value: pct(primary), label: "Primary recommendation", sub: "Primary Rec", raw: primary },
  ];
}

export function AntennaMeasuredFunnel({
  data, loading, failed, brandName, onAskAI,
}: AntennaMeasuredFunnelProps) {
  if (loading) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{ height: 150, borderRadius: 12, background: "var(--muted)", opacity: 0.5 }} />
        ))}
      </div>
    );
  }

  const measured = !failed && data?.source === "measurement" && !!data.funnel;

  if (!measured || !data?.funnel) {
    return (
      <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, padding: 24 }}>
        <div className="flex items-center" style={{ gap: 8, marginBottom: 8 }}>
          <AlertCircle size={15} style={{ color: "var(--muted-foreground)" }} />
          <span style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>Monitoring snapshot</span>
        </div>
        <p style={{ color: "var(--muted-foreground)", fontSize: "var(--text-sm)", lineHeight: 1.6 }}>
          No measurement run is available for this brand yet, so the sections below read
          monitoring data. The funnel appears here once a measurement run completes.
        </p>
      </div>
    );
  }

  // A prospect read stands in for both "latest" and "baseline" while the brand
  // has no client run, but it is never a baseline, and its sequence is
  // negative, so neither "Baseline" nor "Run -2" may be shown for it.
  const isProspectRead = data.run?.isProspectRead === true;
  const isBaselineRun =
    !isProspectRead &&
    data.run != null && data.baselineRun != null && data.run.id === data.baselineRun.id;
  const when = runDate(data.run?.completedAt ?? null);
  const runName = isProspectRead
    ? `Prospect read${data.run?.reps ? ` (${data.run.reps} reps)` : ""}`
    : `Run ${data.run?.sequence ?? ""}`;
  const runChipLabel = `${runName}${when ? ` · ${when}` : ""}`.trim();

  return (
    <div className="flex flex-col" style={{ gap: 12 }}>
      <HeroMetricsRow
        stats={toHeroStats(data.funnel)}
        // No baseline comparison on this surface: the measured-funnel payload
        // carries one run, so there is no prior read to difference against.
        // Deep Signal owns the run-over-run view.
        baselineStats={null}
        isBaselineRun={isBaselineRun}
        question={`How often are models recommending ${brandName || "your brand"}?`}
        runChipLabel={runChipLabel}
        onAskAI={onAskAI}
      />

      {/* What is behind the blend. A lane counts only if it was in this
          client's baseline, so say which ones did. */}
      <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", lineHeight: 1.6, margin: 0 }}>
        Blended across {data.lanes.used.length} model
        {data.lanes.used.length === 1 ? "" : "s"}{" "}
        {isProspectRead ? "in this prospect read" : "in your baseline"}:{" "}
        {data.lanes.used.map((m) => getModelLabel(m)).join(", ")}.
        {data.lanes.notInBaseline.length > 0 && (
          <>
            {" "}
            {data.lanes.notInBaseline.map((m) => getModelLabel(m)).join(", ")} ran this time
            but was not in your baseline, so it is reported separately rather than blended in.
          </>
        )}
      </p>
    </div>
  );
}
